import axios from "axios"
import moment from "moment-timezone"
import React, { useEffect, useState } from "react"
import Swal from "sweetalert2"
import { Dialog } from "primereact/dialog"
import { Calendar } from "primereact/calendar"
import DatePicker from "react-date-picker"
import "react-date-picker/dist/DatePicker.css"
import "react-calendar/dist/Calendar.css"
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { Editor } from "primereact/editor"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Col, Row } from "reactstrap"
import LinkContactsPopup from "ATSComponents/Common/LinkContactsPopup"
import CompanySelector from "ATSComponents/Common/CompanySelector"
import { deliveryErrorMessage, isSuccessfulDelivery } from "../../utils/deliveryResult"

const priorityValue = [
  { name: "Low", value: "low" },
  { name: "Medium", value: "medium" },
  { name: "High", value: "high" },
]

const PANEL_TIMEZONE_OPTIONS = [
  "Asia/Kolkata",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Australia/Sydney",
].map(timezone => ({ label: timezone, value: timezone }))

const SUPPORTED_TIMEZONES = new Set([
  "Asia/Kolkata",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Australia/Sydney",
])

const resolveIanaTimezone = value => {
  const timezone = String(value || "").trim()
  return SUPPORTED_TIMEZONES.has(timezone) ? timezone : "Asia/Kolkata"
}

const getTimezoneFromRecord = record =>
  record?.time_zone ||
  record?.timezone ||
  record?.calendar_timezone ||
  record?.calendar_time_zone ||
  record?.iana_timezone ||
  "Asia/Kolkata"

const getContactId = contact =>
  contact?.id ?? contact?.user_id ?? contact?.employee_id ?? contact?.contact_id

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"
  } catch {
    return "Asia/Kolkata"
  }
}

const formatPanelTimePreview = (date, startTime, endTime, timezone, eventTimezone) => {
  if (!date || !startTime || !endTime || !timezone) return ""

  const start = moment.tz(`${formatDate(date)} ${formatTime(startTime)}`, "YYYY-MM-DD HH:mm:ss", eventTimezone).toDate()
  const end = moment.tz(`${formatDate(date)} ${formatTime(endTime)}`, "YYYY-MM-DD HH:mm:ss", eventTimezone).toDate()

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ""

  const startParts = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  }).formatToParts(start)
  const endParts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
    timeZoneName: "short",
  }).formatToParts(end)
  const getPart = (parts, type) => parts.find(part => part.type === type)?.value || ""
  const dateLabel = `${getPart(startParts, "month")} ${getPart(startParts, "day")}`
  const startClock = `${getPart(startParts, "hour")}:${getPart(startParts, "minute")} ${getPart(startParts, "dayPeriod")}`
  const endClock = `${getPart(endParts, "hour")}:${getPart(endParts, "minute")} ${getPart(endParts, "dayPeriod")}`
  const abbreviation = getPart(endParts, "timeZoneName")
  return `${dateLabel}, ${startClock} - ${endClock} ${abbreviation}`
}

const formatDate = date => {
  if (!date) return ""
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatTime = time => {
  if (!time) return ""
  const t = new Date(time)
  const hours = String(t.getHours()).padStart(2, "0")
  const minutes = String(t.getMinutes()).padStart(2, "0")
  const seconds = String(t.getSeconds()).padStart(2, "0")
  return `${hours}:${minutes}:${seconds}`
}

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

const getCandidateFullName = candidate =>
  [
    candidate?.first_name,
    candidate?.middle_name,
    candidate?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  candidate?.candidate_name ||
  candidate?.name ||
  candidate?.label ||
  candidate?.email ||
  "Candidate"

const getInterviewJobLabel = job =>
  [job?.title, job?.job_number].filter(Boolean).join(", ").trim()

const extractCandidateJobs = candidate => {
  const linkedJobs = Array.isArray(candidate?.linked_jobs) ? candidate.linked_jobs : []

  if (linkedJobs.length > 0) {
    return linkedJobs
      .map(job => ({
        id: job?.job ?? job?.job_id ?? job?.id,
        title: job?.job_name || job?.job_title || job?.title || job?.job || "",
        job_number: job?.job_number || "",
        company_id: candidate?.company || job?.company || candidate?.company_id || null,
      }))
      .filter(job => job.id || job.title)
  }

  const directJobId =
    candidate?.job_id ??
    candidate?.job_ids ??
    candidate?.job?.job_id ??
    candidate?.job?.id ??
    candidate?.linked_job_id ??
    null

  if (directJobId) {
    const normalizedJobIds = String(directJobId).split(",").map(id => id.trim()).filter(Boolean)
    return normalizedJobIds
      .map((jobId, index) => ({
        id: jobId,
        title:
          candidate?.job_titles?.split(",")?.[index]?.trim() ||
          candidate?.job_titles?.trim() ||
          candidate?.job_title?.trim() ||
          "",
        job_number: candidate?.job_number || "",
        company_id: candidate?.company || candidate?.company_id || null,
      }))
      .filter(job => job.id || job.title)
  }

  if (candidate?.job_titles) {
    return String(candidate.job_titles)
      .split(",")
      .map(title => title.trim())
      .filter(Boolean)
      .map((title, index) => ({
        id: `${candidate?.candidate_id || "candidate"}-${index}`,
        title,
        job_number: "",
        company_id: candidate?.company || candidate?.company_id || null,
      }))
  }

  return []
}

const resolveCandidateId = candidate =>
  candidate?.candidate_id ?? candidate?.id ?? candidate?.value ?? null

const resolveCandidateJobId = (candidate, fallbackJobId) => {
  const linkedJobId = Array.isArray(candidate?.linked_jobs)
    ? candidate.linked_jobs.map(job => job?.job_id ?? job?.job ?? job?.id).find(Boolean)
    : null

  const directJobId =
    candidate?.job_id ??
    candidate?.job_ids ??
    candidate?.job?.job_id ??
    candidate?.job?.id ??
    candidate?.linked_job_id ??
    null

  if (directJobId) {
    return (
      String(directJobId).split(",").map(id => id.trim()).filter(Boolean)[0] ||
      fallbackJobId ||
      null
    )
  }

  return linkedJobId ?? fallbackJobId ?? null
}

// ─── Resolve candidate's company ID ──────────────────────────────────────────
const resolveCandidateCompanyId = (candidate, fallbackCompanyId) =>
  candidate?.company ||
  candidate?.company_id ||
  candidate?.company_data?.company_id ||
  candidate?.company_data?.id ||
  fallbackCompanyId ||
  null

const getDefaultInterviewTimes = () => {
  const now = new Date()
  now.setSeconds(0, 0)
  const tenMinutesLater = new Date(now.getTime() + 10 * 60000)
  tenMinutesLater.setSeconds(0, 0)
  const thirtyMinutesLater = new Date(tenMinutesLater.getTime() + 30 * 60000)
  thirtyMinutesLater.setSeconds(0, 0)

  return {
    startDate: tenMinutesLater,
    startTime: tenMinutesLater,
    endDate: tenMinutesLater,
    endTime: thirtyMinutesLater,
  }
}

const ScheduleInterviewDialog = ({
  visible,
  onHide,
  companyId,          // job-level fallback company id
  candidate,
  candidates,
  currentJobId,
  jobTitle,
  onScheduled,
  disableCompany = false,
  candidateReviewHandled = false,
  initialDate = null,
  initialPanelTimezones = {},
}) => {
  const defaultInterviewTimes = getDefaultInterviewTimes()
  const [subtypeCall, setSubTypeCall] = useState([])
  const [scheduleTypeId, setScheduleTypeId] = useState(null)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [scheduleSubtype, setScheduleSubtype] = useState(null)
  const [scheduleStartDate, setScheduleStartdate] = useState(defaultInterviewTimes.startDate)
  const [scheduleStartTime, setScheduleStarttime] = useState(defaultInterviewTimes.startTime)
  const [scheduleEndDate, setScheduleEndDate] = useState(defaultInterviewTimes.endDate)
  const [scheduleEndTime, setScheduleEndTime] = useState(defaultInterviewTimes.endTime)
  const [scheduleSubject, setScheduleSubject] = useState("")
  const [scheduleEventType, setScheduleEventType] = useState("")
  const [schedulePriority, setSchedulePriority] = useState(null)
  const [popTextares, setPopTextares] = useState("")
  const [selectedScheduleJobs, setSelectedScheduleJobs] = useState([])
  const [selectedScheduleContacts, setSelectedScheduleContacts] = useState([])
  const [selectedScheduleContactDetails, setSelectedScheduleContactDetails] = useState([])
  const [panelTimezoneSelections, setPanelTimezoneSelections] = useState({})
  const [clearInterviewerSignal, setClearInterviewerSignal] = useState(0)
  const [selectedScheduleCandidates, setSelectedScheduleCandidates] = useState([])
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [selectedCcUsers, setSelectedCcUsers] = useState([])
  const [scheduleReminder] = useState(null)
  const [scheduleFormErrors, setScheduleFormErrors] = useState({})
  const [loadingSave, setLoadingSave] = useState(false)
  const [candidatePickerVisible, setCandidatePickerVisible] = useState(false)
  const [candidateSearchText, setCandidateSearchText] = useState("")
  const [candidateLazyParams, setCandidateLazyParams] = useState({ page: 0, rows: 10 })
  const [jobCandidates, setJobCandidates] = useState([])
  const [totalJobCandidates, setTotalJobCandidates] = useState(0)
  const [loadingJobCandidates, setLoadingJobCandidates] = useState(false)
  const [selectedCandidateLocal, setSelectedCandidateLocal] = useState(null)
  const initialCandidates =
    Array.isArray(candidates) && candidates.length > 0
      ? candidates
      : candidate
      ? [candidate]
      : []
  const selectedCandidateNames =
    selectedCustomers?.length > 0
      ? selectedCustomers.map(c => getCandidateFullName(c)).join(" | ")
      : ""
  const selectedCandidateItems =
    selectedCustomers?.length > 0 ? selectedCustomers.map(c => getCandidateFullName(c)) : []
  const selectedJobNames = (() => {
    const jobLabels = [
      ...new Set((selectedScheduleJobs || []).map(getInterviewJobLabel).filter(Boolean)),
    ].join(" | ")
    return jobLabels || jobTitle || ""
  })()
  const shouldShowSelectedJob = Boolean(selectedJobNames || currentJobId || initialCandidates.length)
  const selectedJobIdForCandidates =
    selectedScheduleJobs?.[0]?.id ||
    selectedScheduleJobs?.[0]?.job_id ||
    selectedScheduleJobs?.[0]?.job ||
    currentJobId ||
    null

  // ─── Company state ────────────────────────────────────────────────────────
  // selectedCompanyId is the raw numeric/string id we pass around
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)

  // ─── Derive effective company id whenever candidate or prop changes ───────
  useEffect(() => {
    const resolved = resolveCandidateCompanyId(initialCandidates[0] || candidate, companyId)
    setSelectedCompanyId(resolved ? String(resolved) : null)
  }, [candidate, candidates, companyId])

  const fetchInterviewTypes = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-schedule-type/?interview=true")
      if (response.status === 200) {
        const transformedData = response.data.results.map(type => ({
          name: type.schedule_type,
          code: type.id,
        }))
        const interviewType = transformedData.find(t => t.name === "Interview")
        if (interviewType) {
          setScheduleTypeId(interviewType.code)
          setSelectedSchedule(interviewType)
        }
      }
    } catch (error) {
      console.error("Error fetching interview types:", error)
    }
  }

  const fetchSubTypes = async id => {
    if (!id) return
    try {
      const response = await axios.get(`/api/v1/get-all-schedule-sub-type/?schedule_id=${id}`)
      const scheduleData = response.data?.results || []
      const transformedSubTypes = scheduleData.map(schedule => ({
        name: schedule.schedule_subtype || "Unnamed Sub-Type",
        code: schedule.id || "",
      }))
      setSubTypeCall(transformedSubTypes)
    } catch (error) {
      console.error("Error fetching sub-types:", error)
    }
  }

  useEffect(() => {
    if (scheduleTypeId) fetchSubTypes(scheduleTypeId)
  }, [scheduleTypeId])

  const resetForm = () => {
    const defaultTimes = getDefaultInterviewTimes()
    const clickedDate = initialDate ? new Date(initialDate) : null
    const nextStart = clickedDate && !Number.isNaN(clickedDate.getTime()) ? clickedDate : defaultTimes.startDate
    if (clickedDate && nextStart.getHours() === 0 && nextStart.getMinutes() === 0) {
      const defaultStart = defaultTimes.startTime
      nextStart.setHours(defaultStart.getHours(), defaultStart.getMinutes(), 0, 0)
    }
    const nextEnd = new Date(nextStart.getTime() + 30 * 60000)
    setScheduleSubtype(null)
    setScheduleStartdate(nextStart)
    setScheduleStarttime(nextStart)
    setScheduleEndDate(nextStart)
    setScheduleEndTime(nextEnd)
    setScheduleSubject("")
    setScheduleEventType("")
    setSchedulePriority(null)
    setPopTextares("")
    setSelectedScheduleJobs([])
    setSelectedScheduleContacts([])
    setSelectedScheduleContactDetails([])
    setPanelTimezoneSelections({})
    setSelectedScheduleCandidates([])
    setSelectedCustomers([])
    setSelectedCcUsers([])
    setSelectedCandidateLocal(null)
    setCandidateSearchText("")
    setScheduleFormErrors({})
  }

  useEffect(() => {
    if (!visible) return

    fetchInterviewTypes()
    resetForm()

    // Re-resolve company from candidate on each open
    const resolved = resolveCandidateCompanyId(initialCandidates[0] || candidate, companyId)
    setSelectedCompanyId(resolved ? String(resolved) : null)

    if (initialCandidates.length > 0) {
      setSelectedCustomers(initialCandidates)
      setSelectedScheduleCandidates(initialCandidates)
      const candidateJobs = initialCandidates.flatMap(item => extractCandidateJobs(item))
      setSelectedScheduleJobs(
        candidateJobs.length
          ? candidateJobs
          : currentJobId
          ? [{ id: currentJobId, title: jobTitle || "" }]
          : []
      )
      setScheduleSubject(
        initialCandidates.length === 1
          ? `Interview - ${getCandidateFullName(initialCandidates[0])}${jobTitle ? ` - ${jobTitle}` : ""}`
          : `Interview - ${initialCandidates.length} Candidates${jobTitle ? ` - ${jobTitle}` : ""}`
      )
    }
  }, [visible])

  const fetchCandidatesForSelectedJob = async () => {
    if (!selectedJobIdForCandidates || !selectedCompanyId) {
      setJobCandidates([])
      setTotalJobCandidates(0)
      return
    }

    setLoadingJobCandidates(true)
    try {
      const page = candidateLazyParams?.page ?? 0
      const rows = candidateLazyParams?.rows ?? 10
      const response = await axios.get(
        `/api/v1/candidate-jobs/?page=${page + 1}&size=${rows}&job_id=${selectedJobIdForCandidates}&company_id=${selectedCompanyId}`
      )
      const results = response.data?.results || []
      setJobCandidates(
        results.map(item => ({
          ...item,
          id: item.candidate_id || item.id,
          candidate_id: item.candidate_id || item.id,
          first_name: item.name || item.first_name || item.candidate_name || "",
          name: item.name || item.first_name || item.candidate_name || "",
          email: item.email || "",
          designation: item.designation_name || item.designation || "",
          current_company: item.current_company_name || item.current_company || "",
          job_id: selectedJobIdForCandidates,
          company_id: selectedCompanyId,
        }))
      )
      setTotalJobCandidates(response.data?.count || results.length || 0)
    } catch (error) {
      console.error("Error fetching candidates for selected job:", error)
      setJobCandidates([])
      setTotalJobCandidates(0)
    } finally {
      setLoadingJobCandidates(false)
    }
  }

  useEffect(() => {
    if (!visible || !candidatePickerVisible) return
    fetchCandidatesForSelectedJob()
  }, [
    visible,
    candidatePickerVisible,
    selectedJobIdForCandidates,
    selectedCompanyId,
    candidateLazyParams.page,
    candidateLazyParams.rows,
  ])

  const filteredJobCandidates = jobCandidates.filter(item => {
    const query = candidateSearchText.toLowerCase()
    return (
      (item?.first_name || "").toLowerCase().includes(query) ||
      (item?.email || "").toLowerCase().includes(query) ||
      (item?.designation || "").toLowerCase().includes(query) ||
      (item?.current_company || "").toLowerCase().includes(query) ||
      String(item?.candidate_id || item?.id || "").includes(candidateSearchText)
    )
  })

  const candidateTimezone = resolveIanaTimezone(
    getTimezoneFromRecord(selectedScheduleCandidates[0] || initialCandidates[0])
  )
  const userInfo = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("user-info") || sessionStorage.getItem("atsuser-info") || "null")
    } catch {
      return null
    }
  })()
  const recruiterTimezone = resolveIanaTimezone(
    userInfo?.user_details?.time_zone ||
      userInfo?.user_details?.timezone ||
      userInfo?.user_details?.calendar_timezone ||
      "Asia/Kolkata"
  )
  const eventTimezone = resolveIanaTimezone(
    getBrowserTimezone()
  )
  const panelTimezones = Object.fromEntries(
    (selectedScheduleContactDetails || [])
      .map(contact => {
        const id = getContactId(contact)
        return id ? [String(id), panelTimezoneSelections[String(id)] || ""] : null
      })
      .filter(Boolean)
  )
  const panelTimezoneEntries = (selectedScheduleContactDetails || [])
    .map(contact => {
      const id = getContactId(contact)
      if (!id) return null
      const name =
        contact?.name ||
        contact?.full_name ||
        contact?.display_name ||
        contact?.user_name ||
        contact?.email ||
        `Panel member ${id}`
      return {
        id: String(id),
        name,
        timezone: panelTimezones[String(id)] || "",
      }
    })
    .filter(Boolean)

  useEffect(() => {
    if (!selectedScheduleContactDetails?.length) {
      setPanelTimezoneSelections({})
      return
    }

    const browserTimezone = resolveIanaTimezone(getBrowserTimezone())
    setPanelTimezoneSelections(previous => {
      const next = {}
      selectedScheduleContactDetails.forEach(contact => {
        const id = getContactId(contact)
        if (!id) return
        const key = String(id)
        next[key] =
          previous[key] ||
          initialPanelTimezones?.[key] ||
          (SUPPORTED_TIMEZONES.has(getTimezoneFromRecord(contact))
            ? getTimezoneFromRecord(contact)
            : browserTimezone)
      })
      return next
    })
  }, [selectedScheduleContactDetails, initialPanelTimezones])

  useEffect(() => {
    if (!scheduleSubject?.trim() || !scheduleFormErrors.subject) return
    setScheduleFormErrors(prev => ({ ...prev, subject: "" }))
  }, [scheduleSubject, scheduleFormErrors.subject])

  const handleSaveCandidateSelection = () => {
    const selected = selectedCandidateLocal ? [selectedCandidateLocal] : []
    setSelectedCustomers(selected)
    setSelectedScheduleCandidates(selected)
    if (selected.length) {
      setScheduleFormErrors(prev => ({ ...prev, candidates: "" }))
      if (!scheduleSubject) {
        setScheduleSubject(
          `Interview - ${getCandidateFullName(selected[0])}${selectedJobNames ? ` - ${selectedJobNames}` : ""}`
        )
      }
    }
    setCandidatePickerVisible(false)
  }

  const validateScheduleForm = () => {
    const errors = {}
    const now = new Date()

    if (!scheduleSubtype) errors.event_sub_type = "Sub-Type is required"
    if (!selectedCompanyId) errors.company = "Company is required"
    if (!selectedScheduleContacts || selectedScheduleContacts.length === 0) {
      errors.interviewer = "Interviewer is required"
    }
    const selectedPanelIds = selectedScheduleContacts.map(id => String(id))
    const missingPanelTimezone = selectedPanelIds.some(
      id => !PANEL_TIMEZONE_OPTIONS.some(option => option.value === panelTimezoneSelections[id])
    )
    if (selectedPanelIds.length && missingPanelTimezone) {
      errors.panel_timezone = "Please select a timezone for every panel member."
    }
    if (!selectedScheduleJobs || selectedScheduleJobs.length === 0) {
      errors.job_id = "Job is required"
    }
    if (!selectedScheduleCandidates.length) errors.candidates = "Candidate is required"
    if (!scheduleSubject || scheduleSubject.trim() === "") {
      errors.subject = "Subject is required"
    }
    if (!scheduleEventType || !scheduleEventType.trim()) {
      errors.event_type = "Type is required"
    }
    if (!schedulePriority) errors.priority = "Priority is required"

    if (scheduleStartDate && scheduleStartTime && scheduleEndDate && scheduleEndTime) {
      const startDateTime = moment.tz(`${formatDate(scheduleStartDate)} ${formatTime(scheduleStartTime)}`, "YYYY-MM-DD HH:mm:ss", eventTimezone).toDate()

      const resolvedEndDate =
        scheduleEndDate && scheduleEndDate >= scheduleStartDate ? scheduleEndDate : scheduleStartDate
      const endDateTime = moment.tz(`${formatDate(resolvedEndDate)} ${formatTime(scheduleEndTime)}`, "YYYY-MM-DD HH:mm:ss", eventTimezone).toDate()

      if (startDateTime < now) errors.start_time = "Start Time cannot be in the Past Time"
      if (endDateTime <= startDateTime) errors.end_time = "End Time must be after Start Time"
    }

    setScheduleFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveSchedule = async () => {
    const candidatesMissingSalary = selectedScheduleCandidates.filter(candidate => {
      const currentSalary = candidate?.current_salary ?? candidate?.current_ctc ?? candidate?.currentSalary ?? candidate?.currentCTC
      const expectedSalary = candidate?.expected_salary ?? candidate?.expected_ctc ?? candidate?.expectedSalary ?? candidate?.expectedCTC
      const noticePeriod = candidate?.notice_period ?? candidate?.noticePeriod
      const isMissing = value => value === null || value === undefined || String(value).trim() === ""
      return isMissing(currentSalary) || isMissing(expectedSalary) || isMissing(noticePeriod)
    })

    if (!candidateReviewHandled && candidatesMissingSalary.length > 0) {
      const candidateToUpdate = candidatesMissingSalary[0]
      const candidateId = resolveCandidateId(candidateToUpdate)
      const result = await Swal.fire({
        icon: "warning",
        title: "Salary Details Required",
        text: "Please update the current salary, expected salary, and notice period details for this candidate before scheduling the interview.",
        showCancelButton: true,
        confirmButtonText: "Update Salary Details",
        cancelButtonText: "Cancel",
      })

      if (result.isConfirmed && candidateId) {
        window.location.href = `/candidate-editform?candidate_id=${encodeURIComponent(candidateId)}`
      }
      return
    }

    if (!validateScheduleForm()) return
    setLoadingSave(true)

    const effectiveCompanyId = selectedCompanyId || companyId
    const apiUrl = `api/v1/events/?company_id=${effectiveCompanyId}`

    const payload = {
      event_sub_type: scheduleSubtype?.name || "Screening Call",
      event_type: scheduleEventType?.trim() || "Interview",
      end_date: formatDate(
        scheduleEndDate && scheduleEndDate >= scheduleStartDate ? scheduleEndDate : scheduleStartDate
      ),
      start_date: formatDate(scheduleStartDate),
      start_time: formatTime(scheduleStartTime),
      end_time: formatTime(scheduleEndTime),
      candidate_id: selectedScheduleCandidates.map(resolveCandidateId).filter(Boolean),
      candidates: selectedScheduleCandidates.map(c => c.email).filter(Boolean),
      cc_users: selectedCcUsers,
      subject: scheduleSubject,
      description: popTextares,
      job_id: [
        ...new Set(
          selectedScheduleCandidates
            .map(c => resolveCandidateJobId(c, currentJobId))
            .concat(selectedScheduleJobs.map(job => job?.id ?? job?.job_id ?? job?.job))
            .filter(Boolean)
        ),
      ],
      // Keep the scheduler-entered wall-clock value unchanged. The backend
      // interprets it using the explicit event timezone below.
      event_time: `${formatDate(scheduleStartDate)}T${formatTime(scheduleStartTime)}`,
      event_priority: schedulePriority || "Low",
      private: false,
      completed: false,
      body: popTextares,
      contact_id: selectedScheduleContacts,
      reminder: scheduleReminder?.value || 10,
      time_zone: eventTimezone,
      candidate_timezone: candidateTimezone,
      recruiter_timezone: recruiterTimezone,
      panel_timezones: panelTimezones,
    }

    try {
      const response = await axios.post(apiUrl, payload, {})
      if (!isSuccessfulDelivery(response, [200, 201, 202])) {
        await Swal.fire({
          title: "Interview scheduling failed",
          text: deliveryErrorMessage(response.data),
          icon: "error",
          confirmButtonText: "OK",
        })
        return
      }
      Swal.fire({
        title: "Success!",
        text: "Interview scheduled successfully!",
        icon: "success",
        confirmButtonText: "OK",
      })
      resetForm()
      onHide()
      if (onScheduled) onScheduled()
    } catch (error) {
      if (error?.response?.status === 409) {
        setLoadingSave(false)
        const responseData = error.response.data || {}
        const conflictDetails = Array.isArray(responseData.conflict_details)
          ? responseData.conflict_details
          : []
        const primaryConflict = conflictDetails[0]
        const primaryInterviewer = primaryConflict?.interviewer || "The selected interviewer"
        const conflictMessage = `Slot already booked for ${primaryInterviewer}`
        const conflictRows = conflictDetails.flatMap(detail => {
            const interviewer = detail?.interviewer
            const jobTitle = detail?.job_title
            const candidateName = detail?.candidate_name
            const bookedOn = detail?.booked_on || detail?.date
            const startTime = detail?.start_time
            const endTime = detail?.end_time
            const bookedBy = detail?.booked_by
            const bookedByEmail = detail?.booked_by_email
            return [
              `<tr><th>Interviewer</th><td class="schedule-conflict-name">${escapeHtml(interviewer)}</td></tr>`,
              `<tr><th>Job</th><td>${escapeHtml(jobTitle)}</td></tr>`,
              `<tr><th>Candidate</th><td>${escapeHtml(candidateName)}</td></tr>`,
              `<tr><th>Booked on</th><td>${escapeHtml(bookedOn)}, ${escapeHtml(startTime)}–${escapeHtml(endTime)}</td></tr>`,
              `<tr><th>Booked by</th><td>${escapeHtml(bookedBy)}</td></tr>`,
              `<tr><th>Recruiter email</th><td>${escapeHtml(bookedByEmail)}</td></tr>`,
            ]
          }).join("")
        const conflictList = conflictRows
          ? `<div class="schedule-conflict-details"><table class="schedule-conflict-table"><tbody>${conflictRows}</tbody></table></div>`
          : ""

        const conflictResult = await Swal.fire({
          icon: "warning",
          title: conflictMessage,
          html: `<div class="schedule-conflict-message">${conflictList}</div>`,
          showCancelButton: true,
          confirmButtonText: "Choose Another Interviewer",
          cancelButtonText: "Cancel",
          reverseButtons: true,
          customClass: {
            container: "schedule-conflict-swal-container",
            popup: "schedule-conflict-swal",
            icon: "schedule-conflict-swal-icon",
            title: "schedule-conflict-swal-title",
            htmlContainer: "schedule-conflict-swal-content",
            confirmButton: "schedule-conflict-swal-button",
            cancelButton: "schedule-conflict-swal-cancel-button",
          },
          didOpen: () => {
            // SweetAlert must sit above the PrimeReact scheduling dialog.
            const swalContainer = Swal.getContainer()
            if (swalContainer) {
              swalContainer.style.setProperty("z-index", "100000", "important")
              swalContainer.style.position = "fixed"
              swalContainer.style.inset = "0"
            }
          },
        })
        if (conflictResult.isConfirmed) {
          const conflictingNames = new Set(
            conflictDetails.map(detail => String(detail?.interviewer || "").trim().toLowerCase()).filter(Boolean)
          )
          const remainingContacts = selectedScheduleContactDetails.filter(
            contact => !conflictingNames.has(String(contact?.name || "").trim().toLowerCase())
          )
          setSelectedScheduleContacts(
            remainingContacts.length
              ? remainingContacts.map(contact => contact.id)
              : selectedScheduleContacts.length && !selectedScheduleContactDetails.length
              ? selectedScheduleContacts
              : []
          )
          setSelectedScheduleContactDetails(remainingContacts)
          setClearInterviewerSignal(signal => signal + 1)
          setScheduleFormErrors(prev => ({ ...prev, interviewer: "" }))
        }
        return
      }

      const errorMessage = deliveryErrorMessage(error?.response?.data)
      await Swal.fire({
        title: "Error!",
        text: `${errorMessage}`,
        icon: "error",
        confirmButtonText: "OK",
      })
    } finally {
      setLoadingSave(false)
    }
  }

  return (
    <Dialog
      header={
        <div className="schedule-dialog-header">
          <span className="schedule-dialog-header-icon"><i className="pi pi-calendar-plus" /></span>
          <span>
            <strong>Schedule Interview</strong>
            <small>Create a new interview and notify the selected participants.</small>
          </span>
        </div>
      }
      visible={visible}
      className="interview-popup"
      style={{ width: "min(920px, 94vw)" }}
      onHide={() => { onHide() }}
    >
      <form className="schedule-interview-form">
        <div className="schedule-form-section schedule-basics-section">
          <div className="schedule-section-heading">
            <span className="schedule-section-number">1</span>
            <span><strong>Interview details</strong><small>Set the format, company, date, and time.</small></span>
          </div>
          {/* Type + Sub-Type + Company */}
          <Row className="mb-2 g-3">
            <Col xl={4} lg={4}>
              <div className="p-field flex flex-column">
                <label htmlFor="scheduleEventType">
                  Type <span className="text-danger">*</span>
                </label>
                <InputText
                  id="scheduleEventType"
                  value={scheduleEventType}
                  onChange={e => {
                    const nextType = e.target.value
                    setScheduleEventType(nextType)
                    setScheduleSubject(current => {
                      const separatorIndex = current.indexOf(" - ")
                      return separatorIndex >= 0
                        ? `${nextType.trim() || "Interview"}${current.slice(separatorIndex)}`
                        : current
                    })
                    setScheduleFormErrors(prev => ({ ...prev, event_type: "" }))
                  }}
                  placeholder="Enter interview type"
                  className={scheduleFormErrors.event_type ? "p-invalid" : ""}
                />
                {scheduleFormErrors.event_type && (
                  <small className="p-error">{scheduleFormErrors.event_type}</small>
                )}
              </div>
            </Col>
            <Col xl={4} lg={4}>
              <div className="p-field flex flex-column" id="SubTypeDropdown">
                <label htmlFor="integer" className="block">
                  Sub-Type <span className="text-danger">*</span>
                </label>
                <Dropdown
                  value={scheduleSubtype}
                  onChange={e => {
                    setScheduleSubtype(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, event_sub_type: "" }))
                  }}
                  options={subtypeCall}
                  optionLabel="name"
                  placeholder="Subtype"
                  className="w-full search-option"
                />
                {scheduleFormErrors.event_sub_type && (
                  <small className="p-error">{scheduleFormErrors.event_sub_type}</small>
                )}
              </div>
            </Col>
            <Col xl={4} lg={4}>
              <div className="p-field flex flex-column">
                <label htmlFor="company" className="block">
                  Company <span className="text-danger">*</span>
                </label>
                <CompanySelector
                  selectedCompanyId={selectedCompanyId}
                  disabled
                  onCompanyChange={company => {
                    const id =
                      company?.company_id ??
                      company?.id ??
                      company?.code ??
                      null
                    setSelectedCompanyId(id ? String(id) : null)
                    setScheduleFormErrors(prev => ({ ...prev, company: "" }))
                  }}
                />
                {scheduleFormErrors.company && (
                  <small className="p-error">{scheduleFormErrors.company}</small>
                )}
              </div>
            </Col>
          </Row>

          {/* Date + Time */}
          <Row className="mb-2 g-3">
            <Col xl={4} lg={4}>
              <div className="p-field flex-auto">
                <label htmlFor="startDate" className="block">
                  Start Date <span className="text-danger">*</span>
                </label>
                <DatePicker
                  id="startDate"
                  className="schedule-date-picker"
                  value={scheduleStartDate}
                  onChange={value => {
                    const nextDate = value instanceof Date ? new Date(value) : value
                    setScheduleStartdate(nextDate)
                    setScheduleEndDate(nextDate instanceof Date ? new Date(nextDate) : nextDate)
                    setScheduleFormErrors(prev => ({ ...prev, start_time: "", end_time: "" }))
                  }}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  format="y-MM-dd"
                  clearIcon={null}
                  calendarIcon={<i className="pi pi-calendar" />}
                />
              </div>
            </Col>
            <Col xl={4} lg={4}>
              <div className="p-field flex-auto">
                <label htmlFor="startTime" className="block">
                  Start Time <span className="text-danger">*</span>
                </label>
                <Calendar
                  id="startTime"
                  className="w-100"
                  value={scheduleStartTime}
                  onChange={e => {
                    setScheduleStarttime(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, start_time: "", end_time: "" }))
                  }}
                  showIcon
                  timeOnly
                  hourFormat="12"
                  icon={() => <i className="pi pi-clock" />}
                />
                {scheduleFormErrors.start_time && (
                  <small className="p-error">{scheduleFormErrors.start_time}</small>
                )}
              </div>
            </Col>
            <Col xl={4} lg={4}>
              <div className="flex-auto">
                <label htmlFor="endTime" className="block">
                  End Time <span className="text-danger">*</span>
                </label>
                <Calendar
                  id="endTime"
                  className="w-100"
                  value={scheduleEndTime}
                  onChange={e => {
                    setScheduleEndTime(e.value)
                    setScheduleFormErrors(prev => ({ ...prev, end_time: "" }))
                  }}
                  showIcon
                  timeOnly
                  hourFormat="12"
                  icon={() => <i className="pi pi-clock" />}
                />
                {scheduleFormErrors.end_time && (
                  <small className="p-error">{scheduleFormErrors.end_time}</small>
                )}
              </div>
            </Col>
          </Row>
        </div>

        <div className="schedule-form-section schedule-participants-section">
          <div className="schedule-section-heading">
            <span className="schedule-section-number">2</span>
            <span><strong>Participants and preferences</strong><small>Select the candidate, panel, subject, and priority.</small></span>
          </div>
          <Row className="mb-2">
            <LinkContactsPopup
              hideJobSelector={initialCandidates.length > 0}
              onSelectedJobsChange={jobs => {
                setSelectedScheduleJobs(jobs)
                setSelectedCustomers([])
                setSelectedScheduleCandidates([])
                setSelectedCandidateLocal(null)
                setScheduleFormErrors(prev => ({ ...prev, job_id: "" }))
              }}
              errorJob={scheduleFormErrors.job_id}
              setSelectedScheduleContacts={contacts => {
                setSelectedScheduleContacts(contacts)
                setScheduleFormErrors(prev => ({ ...prev, interviewer: "" }))
              }}
              setSelectedScheduleContactDetails={contacts => setSelectedScheduleContactDetails(contacts)}
              clearSelectedContactsSignal={clearInterviewerSignal}
              setSelectedCcUsers={setSelectedCcUsers}
              clearInterviewerError={() =>
                setScheduleFormErrors(prev => ({ ...prev, interviewer: "" }))
              }
              errorContact={scheduleFormErrors.interviewer}
              // pass the resolved company id so LinkContactsPopup fetches the right interviewers
              companyId={selectedCompanyId || companyId}
              jobLabel={
                <span>
                  Jobs <span className="text-danger">*</span>
                </span>
              }
            >
              <Col xl={6} style={{ order: 4 }}>
                <div className="field" title={selectedCandidateNames}>
                  <label htmlFor="selectedCandidate" style={{ marginBottom: "4px" }}>
                    Candidate <span className="text-danger">*</span>
                  </label>
                  <div
                    id="selectedCandidate"
                    title={selectedCandidateNames}
                    onClick={() => {
                      if (!selectedJobIdForCandidates) {
                        setScheduleFormErrors(prev => ({ ...prev, job_id: "Select Job first" }))
                        return
                      }
                      setCandidatePickerVisible(true)
                    }}
                    style={{
                      minHeight: 38,
                      width: "100%",
                      border: "1px solid #ced4da",
                      borderRadius: 4,
                      background: "#f8fafc",
                      padding: "6px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                      cursor: selectedJobIdForCandidates ? "pointer" : "not-allowed",
                    }}
                  >
                    {selectedCandidateItems.length ? (
                      selectedCandidateItems.map((name, index) => (
                        <span
                          key={`${name}-${index}`}
                          title={name}
                          style={{
                            maxWidth: "100%",
                            background: "#e7f1fb",
                            border: "1px solid #b9d7f2",
                            color: "#0e527d",
                            borderRadius: 999,
                            padding: "3px 10px",
                            fontSize: 12,
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: 13 }}>Candidate</span>
                    )}
                  </div>
                  {scheduleFormErrors.candidates && (
                    <small className="p-error">{scheduleFormErrors.candidates}</small>
                  )}
                </div>
              </Col>
              <Col xl={6} style={{ order: 5 }}>
                {shouldShowSelectedJob ? (
                  <div className="field" title={selectedJobNames}>
                    <label htmlFor="selectedJob" style={{ marginBottom: "4px" }}>
                      Job <span className="text-danger">*</span>
                    </label>
                    <InputText
                      id="selectedJob"
                      disabled
                      className="w-full"
                      value={selectedJobNames}
                      placeholder="Job titles will appear here"
                    />
                    {scheduleFormErrors.job_id && (
                      <small className="p-error">{scheduleFormErrors.job_id}</small>
                    )}
                  </div>
                ) : null}
              </Col>
              <Col xl={6} style={{ order: 6 }}>
                <div className="p-field flex flex-column mt-3">
                  <label htmlFor="subject">
                    Subject <span className="text-danger">*</span>
                  </label>
                  <InputText
                    placeholder="Subject"
                    value={scheduleSubject}
                    onChange={e => {
                      setScheduleSubject(e.target.value)
                      if (scheduleFormErrors.subject) {
                        setScheduleFormErrors(prev => ({ ...prev, subject: "" }))
                      }
                    }}
                  />
                  {scheduleFormErrors.subject && (
                    <small className="p-error">{scheduleFormErrors.subject}</small>
                  )}
                </div>
              </Col>
              <Col xl={6} style={{ order: 7 }}>
                <div className="p-field flex flex-column mt-3" id="PriorityDropdown">
                  <label htmlFor="Priority" className="block">
                    Priority <span className="text-danger">*</span>
                  </label>
                  <Dropdown
                    value={schedulePriority}
                    onChange={e => {
                      setSchedulePriority(e.value)
                      if (scheduleFormErrors.priority) {
                        setScheduleFormErrors(prev => ({ ...prev, priority: "" }))
                      }
                    }}
                    options={priorityValue}
                    optionLabel="name"
                    placeholder="Priority"
                    className="w-full search-option"
                  />
                  {scheduleFormErrors.priority && (
                    <small className="p-error">{scheduleFormErrors.priority}</small>
                  )}
                </div>
              </Col>
            </LinkContactsPopup>
          </Row>

          <div
            className="schedule-timezone-summary schedule-panel-timezones"
            style={{
              display: "block",
              marginTop: 8,
              padding: "10px 12px",
              border: "1px solid #d9e7f5",
              borderRadius: 8,
              background: "#f8fbff",
              color: "#334155",
              fontSize: 12,
            }}
          >
              <div className="schedule-timezone-heading-row">
                <strong className="schedule-timezone-heading">Panel timezones</strong>
                <small>Choose how each panel member sees the meeting time.</small>
              </div>
            {panelTimezoneEntries.length ? (
              <div className="schedule-timezone-grid">
              {panelTimezoneEntries.map(panel => (
                <label key={panel.id} className="schedule-timezone-field">
                  <span className="schedule-timezone-name-row">
                    <span className="schedule-timezone-name">{panel.name}</span>
                    {panel.timezone && (
                      <small className="schedule-timezone-preview">
                        {" - "}
                        {formatPanelTimePreview(
                          scheduleStartDate,
                          scheduleStartTime,
                          scheduleEndTime,
                          panel.timezone,
                          eventTimezone
                        )}
                      </small>
                    )}
                  </span>
                  <Dropdown
                    value={panelTimezoneSelections[panel.id] || null}
                    options={PANEL_TIMEZONE_OPTIONS}
                    onChange={event => {
                      setPanelTimezoneSelections(previous => ({
                        ...previous,
                        [panel.id]: event.value,
                      }))
                      setScheduleFormErrors(previous => ({ ...previous, panel_timezone: "" }))
                    }}
                    placeholder="Select timezone"
                    aria-label={`Timezone for ${panel.name}`}
                    className="schedule-timezone-dropdown"
                  />
                </label>
              ))}
              </div>
            ) : (
              <span>No panel members selected</span>
            )}
            {scheduleFormErrors.panel_timezone && (
              <small className="p-error" style={{ flexBasis: "100%" }}>
                {scheduleFormErrors.panel_timezone}
              </small>
            )}
          </div>

          {/* Note field removed: not used in schedule interview flow.
          <Row className="mb-2">
            <Col xl={12}>
              <div>
                <label htmlFor="note">Note :</label>
                <Editor
                  value={popTextares}
                  onTextChange={e => setPopTextares(e.htmlValue || "")}
                  placeholder="Interview Notes"
                  style={{ height: "140px" }}
                />
              </div>
            </Col>
          </Row>
          */}
        </div>

        <Row className="schedule-form-actions">
          <Col xl={12}>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light me-2"
                onClick={() => { onHide() }}
              >
                <i className="pi pi-times me-1"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light btn btn-primary me-2 btn-main"
                onClick={handleSaveSchedule}
                disabled={loadingSave}
              >
                <i className="pi pi-save me-1"></i>
                {loadingSave ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Scheduling...
                  </>
                ) : (
                  "Schedule Interview"
                )}
              </button>
            </div>
          </Col>
        </Row>
      </form>

      <Dialog
        header="Link Candidate"
        visible={candidatePickerVisible}
        onHide={() => setCandidatePickerVisible(false)}
        style={{ width: "42vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        className="cand-details"
      >
        <Row>
          <Col lg={12}>
            <div className="p-inputgroup mb-3" style={{ position: "relative" }}>
              <InputText
                placeholder="Search Candidates"
                value={candidateSearchText}
                onInput={e => setCandidateSearchText(e.target.value)}
                style={{ paddingRight: "2rem" }}
              />
              <i
                className="pi pi-search"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              ></i>
            </div>
          </Col>
        </Row>
        <div id="CandidateInfo">
          <DataTable
            value={filteredJobCandidates}
            selection={selectedCandidateLocal}
            onSelectionChange={e => setSelectedCandidateLocal(e.value)}
            dataKey="id"
            size="small"
            lazy
            paginator
            first={candidateLazyParams.page * candidateLazyParams.rows}
            rows={candidateLazyParams.rows}
            totalRecords={totalJobCandidates}
            onPage={event => setCandidateLazyParams({ page: event.page, rows: event.rows })}
            loading={loadingJobCandidates}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            rowsPerPageOptions={[10, 20, 50, totalJobCandidates].filter(Boolean)}
          >
            <Column selectionMode="single" style={{ width: "3em" }} />
            <Column field="first_name" header="Candidate Name" body={row => row.first_name || row.name || "-"} />
            <Column field="email" header="Email" body={row => row.email || "-"} />
            <Column field="designation" header="Designation" body={row => row.designation || "-"} />
            <Column field="current_company" header="Company" body={row => row.current_company || "-"} />
          </DataTable>
        </div>
        <div className="d-flex justify-content-center gap-2 mt-3">
          <button type="button" className="btn btn-primary btn-main" onClick={handleSaveCandidateSelection}>
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSelectedCandidateLocal(null)
              setCandidateSearchText("")
            }}
          >
            Refresh
          </button>
        </div>
      </Dialog>
    </Dialog>
  )
}

export default ScheduleInterviewDialog
